import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CoinFlipBackground = () => {
  const [floatingTokens, setFloatingTokens] = useState([]);

  // Memoize mobile detection for performance
  const isMobile = useMemo(() => window.innerWidth < 768, []);

  // NOTE: Stars/nebula are now handled by the dedicated <StarfieldBackground />
  // (animated Canvas warp field) for smoother performance. This component keeps
  // only the deep-space base gradient and the floating SOLVIBE coin tokens.

  // Generate diverse movement paths for SOLVIBE tokens - optimized with varied directions
  const generateTokenPath = () => {
    const isMobile = window.innerWidth < 768;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    const pathTypes = [
      // Meteor/shooting star style - fast diagonal
      { 
        startX: -150, startY: Math.random() * 20, 
        endX: screenWidth + 150, endY: Math.random() * 60 + 40,
        rotation: 35, speed: 'fast', style: 'meteor'
      },
      { 
        startX: screenWidth + 150, startY: Math.random() * 20, 
        endX: -150, endY: Math.random() * 60 + 40,
        rotation: -35, speed: 'fast', style: 'meteor'
      },
      
      // Crossing diagonal paths
      { 
        startX: -120, startY: screenHeight * 0.8, 
        endX: screenWidth + 120, endY: Math.random() * 30,
        rotation: -25, speed: 'medium', style: 'cross'
      },
      { 
        startX: screenWidth + 120, startY: screenHeight * 0.8, 
        endX: -120, endY: Math.random() * 30,
        rotation: 25, speed: 'medium', style: 'cross'
      },
      
      // Gentle arc movements - slower and atmospheric
      { 
        startX: -100, startY: Math.random() * 50 + 30, 
        endX: screenWidth + 100, endY: Math.random() * 50 + 20,
        rotation: 15, speed: 'slow', style: 'arc'
      },
      { 
        startX: screenWidth + 100, startY: Math.random() * 50 + 20, 
        endX: -100, endY: Math.random() * 50 + 30,
        rotation: -15, speed: 'slow', style: 'arc'
      },
      
      // Vertical shooting paths (less common)
      { 
        startX: Math.random() * screenWidth * 0.8 + screenWidth * 0.1, startY: -150, 
        endX: Math.random() * screenWidth * 0.8 + screenWidth * 0.1, endY: screenHeight + 150,
        rotation: 90, speed: 'medium', style: 'vertical'
      },
      
      // Steep diagonal meteor strikes
      { 
        startX: Math.random() * 100, startY: -120, 
        endX: screenWidth - Math.random() * 100, endY: screenHeight + 120,
        rotation: 60, speed: 'fast', style: 'strike'
      }
    ];

    // Mobile optimization - prefer horizontal paths
    if (isMobile) {
      return pathTypes.filter(path => 
        path.style === 'meteor' || path.style === 'arc' || path.style === 'cross'
      )[Math.floor(Math.random() * 6)];
    }

    return pathTypes[Math.floor(Math.random() * pathTypes.length)];
  };

  // Memoized token generation for performance
  const generateFloatingToken = useCallback(() => {
    const id = Math.random();
    const path = generateTokenPath();
    
    // Speed-based duration mapping
    const getDuration = (speed) => {
      switch(speed) {
        case 'fast': return Math.random() * 2 + 3; // 3-5 seconds
        case 'medium': return Math.random() * 3 + 5; // 5-8 seconds  
        case 'slow': return Math.random() * 4 + 7; // 7-11 seconds
        default: return Math.random() * 3 + 5;
      }
    };
    
    // Optimized opacity for better visibility
    const baseOpacity = path.speed === 'fast' ? 0.7 : 0.5;
    const opacity = Math.random() * 0.3 + baseOpacity;
    
    const newToken = {
      id,
      startX: path.startX,
      startY: path.startY,
      endX: path.endX,
      endY: path.endY,
      duration: getDuration(path.speed),
      delay: Math.random() * 0.2, // Minimal delay for immediate appearance
      opacity: opacity,
      rotation: path.rotation + (Math.random() * 20 - 10),
      rotationSpeed: path.speed === 'fast' ? Math.random() * 1 + 1.5 : Math.random() * 1 + 0.5,
      size: isMobile ? Math.random() * 8 + 25 : Math.random() * 12 + 30,
      glowIntensity: opacity > 0.6 ? opacity * 0.8 : opacity * 0.6,
      speed: path.speed,
      style: path.style
    };
    
    setFloatingTokens(prev => [...prev, newToken]);
    
    // Cleanup with proper timing
    setTimeout(() => {
      setFloatingTokens(prev => prev.filter(token => token.id !== id));
    }, (newToken.duration + newToken.delay + 0.5) * 1000);
  }, [isMobile]);

  // Performance-optimized token generation with controlled frequency
  useEffect(() => {
    // Delay animations on mobile to prioritize content loading
    const startDelay = isMobile ? 1000 : 0;
    
    const initAnimations = () => {
      // Initial tokens — slightly more on mobile so animation is visible
      const initialTokenCount = isMobile ? 2 : 3;

      // Generate initial tokens with optimal spacing
      for (let i = 0; i < initialTokenCount; i++) {
        setTimeout(() => generateFloatingToken(), i * 800);
      }

      // Controlled interval generation with performance monitoring
      const generateNewToken = () => {
        const currentTokenCount = floatingTokens.length;
        const maxTokens = isMobile ? 3 : 4;

        // Higher chance on mobile (fewer tokens overall, so we want them visible)
        const spawnChance = isMobile ? 0.7 : 0.3;
        if (currentTokenCount < maxTokens && Math.random() < spawnChance) {
          generateFloatingToken();
        }
      };

      // Optimized intervals - longer for subtlety and performance
      const tokenInterval = setInterval(generateNewToken, isMobile ? 6000 : 4500);

      return () => {
        clearInterval(tokenInterval);
      };
    };

    // Use requestIdleCallback on mobile for better performance
    let cleanup;
    if (isMobile && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        cleanup = initAnimations();
      }, { timeout: 2000 });
    } else {
      const timer = setTimeout(() => {
        cleanup = initAnimations();
      }, startDelay);
      return () => clearTimeout(timer);
    }

    return cleanup;
  }, [generateFloatingToken, isMobile]); // Only depend on callback and mobile status

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {/* Immersive Galaxy Background */}
      <div className="absolute inset-0" style={{ zIndex: -10, willChange: 'auto', transform: 'translateZ(0)' }}>
        {/* Deep space base gradient - optimized */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse at 30% 20%, rgba(67, 56, 202, 0.08) 0%, transparent 60%),
              radial-gradient(ellipse at 70% 60%, rgba(6, 182, 212, 0.06) 0%, transparent 60%),
              radial-gradient(ellipse at 20% 80%, rgba(147, 51, 234, 0.07) 0%, transparent 60%),
              radial-gradient(ellipse at 90% 30%, rgba(8, 145, 178, 0.05) 0%, transparent 60%),
              linear-gradient(135deg, 
                #020204 0%, 
                #0a0a12 20%, 
                #0f1419 40%, 
                #0a0a12 60%, 
                #020204 80%,
                #000000 100%
              )
            `,
            willChange: 'auto',
          }}
        />

        {/* Stars & nebula now rendered by <StarfieldBackground /> for performance */}
      </div>

      {/* Floating SOLVIBE Token Animation — enabled on all devices.
          Mobile uses fewer tokens (max 3), smaller sizes and longer intervals
          to keep GPU/battery load light while still showing the animation. */}
      <AnimatePresence mode="popLayout">
        {floatingTokens.map((token) => (
          <motion.div
            key={token.id}
            initial={{
              opacity: 0,
              x: token.startX,
              y: token.startY,
              scale: 0.2,
              rotate: token.rotation,
            }}
            animate={{
              opacity: token.speed === 'fast'
                ? [0, token.opacity * 0.6, token.opacity, token.opacity * 0.4, 0]
                : [0, token.opacity * 0.3, token.opacity, token.opacity * 0.6, token.opacity * 0.3, 0],
              x: token.endX,
              y: token.endY,
              scale: token.speed === 'fast'
                ? [0.2, 1, 1, 0.3]
                : [0.2, 0.7, 1, 1, 0.8, 0.2],
              rotate: [
                token.rotation,
                token.rotation + (90 * token.rotationSpeed),
                token.rotation + (180 * token.rotationSpeed)
              ],
            }}
            exit={{
              opacity: 0,
              scale: 0.1,
              transition: { duration: 0.3 }
            }}
            transition={{
              duration: token.duration,
              delay: token.delay,
              ease: token.speed === 'fast' ? "easeOut" : "easeInOut",
              times: token.speed === 'fast'
                ? [0, 0.2, 0.6, 0.9, 1]
                : [0, 0.1, 0.3, 0.7, 0.9, 1],
            }}
            className="absolute will-change-transform"
            style={{
              width: `${token.size}px`,
              height: `${token.size}px`,
              zIndex: -3,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              borderRadius: '50%',
              overflow: 'hidden',
            }}
          >
            {/* Lokales 17KB-Logo statt 1.47MB-Hotlink (1024px-Bild für ~41px
                Anzeige, externe Domain ohne Cache) — größter Einzel-Download
                der Seite laut PageSpeed. Gleiches SolVibe-Coin-Motiv. */}
            <img
              src="/logos/SolVibe-Logo.web.png"
              alt=""
              loading="lazy"
              className="w-full h-full object-contain"
              style={{
                opacity: token.opacity,
                filter: token.speed === 'fast'
                  ? `brightness(1.4) contrast(1.2) saturate(1.3)`
                  : `brightness(1.2) contrast(1.1) saturate(1.2)`,
                boxShadow: token.speed === 'fast'
                  ? `0 0 ${20 * token.glowIntensity}px rgba(67, 56, 202, ${token.glowIntensity * 0.4}),
                     0 0 ${30 * token.glowIntensity}px rgba(6, 182, 212, ${token.glowIntensity * 0.3})`
                  : `0 0 ${12 * token.glowIntensity}px rgba(67, 56, 202, ${token.glowIntensity * 0.2}),
                     0 0 ${20 * token.glowIntensity}px rgba(6, 182, 212, ${token.glowIntensity * 0.15})`,
                borderRadius: '50%',
                mixBlendMode: token.speed === 'fast' ? 'screen' : 'normal',
                transform: 'translateZ(0)',
              }}
            />
            {token.speed === 'fast' && token.opacity > 0.6 && (
              <div
                className="absolute inset-0"
                style={{
                  background: `radial-gradient(circle, rgba(67, 56, 202, ${token.glowIntensity * 0.08}) 0%, transparent 60%)`,
                  transform: 'scale(1.8) translateZ(0)',
                  filter: 'blur(8px)',
                  zIndex: -1,
                }}
              />
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* CSS Keyframes for cosmic animations */}
      <style jsx>{`
        @keyframes starBlink {
          0%, 100% { opacity: 0.2; transform: scale(0.8); }
          25% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
          75% { opacity: 0.7; transform: scale(1); }
        }

        @keyframes starPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 0.8; transform: scale(1.2); }
        }

        @keyframes starDrift {
          0% { transform: translateX(0) translateY(0); }
          25% { transform: translateX(2px) translateY(-1px); }
          50% { transform: translateX(-1px) translateY(2px); }
          75% { transform: translateX(1px) translateY(1px); }
          100% { transform: translateX(0) translateY(0); }
        }

        @keyframes nebulaDrift {
          0%, 100% { transform: translateX(0) translateY(0) scale(1); }
          33% { transform: translateX(10px) translateY(-5px) scale(1.02); }
          66% { transform: translateX(-5px) translateY(8px) scale(0.98); }
        }

        @keyframes cosmicDrift {
          0%, 100% { transform: translateX(0) translateY(0) rotate(0deg); }
          50% { transform: translateX(-15px) translateY(10px) rotate(1deg); }
        }
      `}</style>
    </div>
  );
};

export default CoinFlipBackground;